import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, Outlet, useNavigate } from 'react-router-dom';

import { PermissionKeys, useGetProductsQuery } from '@client/api';
import {
  Card,
  EmptyPlaceholder,
  PageTitle,
  Pagination,
  Table,
  TableBody,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  useRouteDocumentTitle,
} from '@client/toolkit';

import { ProductListRow, ProductListToolbar } from './components';
import { useProductListSearchQueryParams, useProductListSortQueryParams } from './utils/use-product-list-query-params';

import { ProductsPaths } from '../products.router';
import { PageListLayout, PageContentAnimation } from '../../../components/layout';
import { PermissionBasedActionButton } from '../../../components/permission-based-button/permission-based-button';

export const ProductListRoute = () => {
  const { t } = useTranslation('products');
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');
  useRouteDocumentTitle(t('documentTitle'), ProductsPaths.Products);

  const navigate = useNavigate();

  const [sortQueryParams] = useProductListSortQueryParams();
  const [searchQueryParams] = useProductListSearchQueryParams();

  const pageSize = 30;
  const [page, setPage] = useState(0);

  const { data, isLoading, isFetching } = useGetProductsQuery(
    {
      'pagination.Limit': pageSize,
      'pagination.Offset': page * pageSize,
      search: searchQueryParams.productsSearch ? searchQueryParams.productsSearch : undefined,
      'sort.Property': sortQueryParams.productsSortProperty ? sortQueryParams.productsSortProperty : undefined,
      'sort.Order': sortQueryParams.productsSortOrder ? sortQueryParams.productsSortOrder : undefined,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const products = useMemo(() => data?.items ?? [], [data?.items]);

  const totalCount = data?.totalCount ?? 0;

  const handleClickCreateProduct = () => navigate(generatePath(ProductsPaths.ProductCreate));

  return (
    <PageListLayout breadcrumb={tGlobal('navigation.overview')}>
      <div className="flex items-start justify-between">
        <PageTitle className="pb-2" />
        <PermissionBasedActionButton
          color="primary-dark"
          size="md"
          onClick={handleClickCreateProduct}
          permissions={[PermissionKeys.Tenant.ProductCreate]}
          title={tCommon('createEntity', { entity: tGlobal('entity.product').toLowerCase() })}
        />
      </div>
      <PageContentAnimation>
        <Card overflowX={false}>
          <ProductListToolbar totalCount={totalCount} />
          <Table>
            {!isLoading && products.length <= 0 && (
              <caption className="px-4 py-4 [caption-side:bottom]">
                <EmptyPlaceholder>
                  {tCommon('noEntityFound', { entity: tGlobal('entity.product', { context: 'plural' }) })}
                </EmptyPlaceholder>
              </caption>
            )}

            <TableHead>
              <TableHeadRow>
                <TableHeadCell>{tGlobal('product.name')}</TableHeadCell>
                <TableHeadCell>{tGlobal('product.category')}</TableHeadCell>
                <TableHeadCell>{tGlobal('product.unitScheme')}</TableHeadCell>
                <TableHeadCell align="right">{tGlobal('product.description')}</TableHeadCell>
              </TableHeadRow>
            </TableHead>
            <TableBody className="border-b">
              {isLoading && Array.from(Array(10).keys()).map((x) => <ProductListRow key={x} isFetching />)}

              {!isLoading &&
                products.length > 0 &&
                products.map((product) => (
                  <ProductListRow key={product.id} product={product} isFetching={isFetching} />
                ))}
            </TableBody>
          </Table>

          <Pagination totalCount={totalCount} currentPage={page} setPage={setPage} pageSize={pageSize} />
        </Card>
      </PageContentAnimation>
      <Outlet />
    </PageListLayout>
  );
};
